import React from 'react';
import { Page, View, Text, StyleSheet } from '@react-pdf/renderer';
import { renderPdfElements, PdfElement, PdfListItem } from '../../pdfHtmlImageUtils';
import { ReportData } from '../../../types/report.types';
import { normalizeSeverity, processAttachmentsToElements } from '../../../utils/findingsUtils';
import { CVSS31 } from '../../../../../utils/cvss31/cvsscalc31';
import { CVSS40 } from '@pandatix/js-cvss';
import { parseCategoryString } from '../../../../../utils/categoryUtils';

interface DetailedFindingsBySeverityPageProps {
  severity: string;
  findings: any[];
  documentNumber?: string;
  reportData?: ReportData;
  sectionId?: string;
  showMainTitle?: boolean;
}

const severityColors: Record<string, string> = {
  Critical: '#E11D48',
  High: '#F97316',
  Medium: '#EAB308',
  Low: '#16A34A',
  Info: '#8B9BB4',
};

const styles = StyleSheet.create({
  page: {
    flexDirection: 'column',
    backgroundColor: '#FFFFFF',
    padding: '16mm 14mm',
    fontFamily: 'Roboto',
    fontSize: 9,
    color: '#374151',
  },
  header: {
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB',
    paddingBottom: 6,
    marginBottom: 12,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  headerTitle: {
    fontSize: 10,
    fontWeight: 'bold',
    color: '#0F172A',
    textTransform: 'uppercase',
    letterSpacing: 1,
  },
  headerMeta: {
    fontSize: 8,
    color: '#64748B',
  },
  // Main Section Title at top of first page
  mainTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#0F172A',
  },
  titleUnderlineRow: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    width: '100%',
    height: 3,
    marginTop: 4,
    marginBottom: 8,
  },
  titleUnderlineBlue: {
    width: 60,
    height: 3,
    backgroundColor: '#2563EB',
  },
  titleUnderlineDark: {
    flex: 1,
    height: 1.2,
    backgroundColor: '#1E293B',
  },
  mainSubtitle: {
    fontSize: 9,
    color: '#9CA3AF',
    marginBottom: 16,
  },
  // Card layout
  card: {
    borderWidth: 1,
    borderColor: '#E5E7EB',
    borderRadius: 8,
    padding: 14,
    marginBottom: 20,
    backgroundColor: '#FFFFFF',
  },
  cardHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingBottom: 8,
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB',
    marginBottom: 12,
  },
  cardHeaderLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  cardHeaderRight: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  cardFindingId: {
    fontSize: 10,
    fontWeight: 'bold',
    color: '#9CA3AF',
  },
  cardTitle: {
    fontSize: 11,
    fontWeight: 'bold',
    color: '#0F172A',
    marginLeft: 8,
  },
  severityPill: {
    borderRadius: 8,
    paddingVertical: 2.5,
    paddingHorizontal: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  severityPillText: {
    color: '#FFFFFF',
    fontSize: 7.5,
    fontWeight: 'bold',
    lineHeight: 1.1,
    textAlign: 'center',
  },
  cvssLabel: {
    fontSize: 9.5,
    color: '#9CA3AF',
  },
  // Details grid under header
  detailsGrid: {
    marginBottom: 12,
    gap: 6,
  },
  detailsRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  detailsLabel: {
    width: '22%',
    fontSize: 7.5,
    color: '#9CA3AF',
    textTransform: 'uppercase',
    fontWeight: 'bold',
    letterSpacing: 0.5,
  },
  detailsValue: {
    width: '78%',
    fontSize: 8.5,
    color: '#1F2937',
  },
  cvssVectorPill: {
    backgroundColor: '#F1F5F9',
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 4,
    paddingVertical: 2,
    paddingHorizontal: 6,
    alignSelf: 'flex-start',
  },
  cvssVectorPillText: {
    color: '#475569',
    fontSize: 7.5,
    fontFamily: 'Courier',
  },
  // Content sections
  sectionBox: {
    marginTop: 10,
    marginBottom: 6,
  },
  sectionTitle: {
    fontSize: 11.0,
    fontWeight: 'bold',
    color: '#2563EB',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginBottom: 4,
  },
  contentWrapper: {
    fontSize: 10.5,
    lineHeight: 1.50,
    color: '#374151',
  },
  // Burp/sqlmap Image placeholder matching mockup
  imagePlaceholderBox: {
    borderWidth: 1,
    borderStyle: 'dashed',
    borderColor: '#CBD5E1',
    backgroundColor: '#F8FAFC',
    borderRadius: 6,
    padding: 12,
    marginTop: 6,
    alignItems: 'center',
    justifyContent: 'center',
    height: 48,
  },
  imagePlaceholderText: {
    color: '#9CA3AF',
    fontSize: 8,
    textAlign: 'center',
  },
  footer: {
    position: 'absolute',
    bottom: 20,
    left: 0,
    right: 0,
    fontSize: 8.5,
    color: '#9CA3AF',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 14,
  },
  footerLeft: {
    textAlign: 'left',
    flex: 1,
  },
  footerRight: {
    textAlign: 'right',
    flex: 1,
  },
});

function sanitizeHtml(html: string): string {
  return html.replace(/(on\w+|javascript:|data-(?!language))="[^"]*"/gi, '');
}

function parseHtmlToElementsSync(htmlString: string): PdfElement[] {
  if (!htmlString) return [];
  if (typeof window === 'undefined' || typeof DOMParser === 'undefined') {
    return [{ type: 'text', content: htmlString.replace(/<[^>]+>/g, '') }];
  }

  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlString, 'text/html');
  const elements: PdfElement[] = [];

  const processNode = (node: Node, container: PdfElement[] = elements): void => {
    if (node.nodeType === Node.TEXT_NODE) {
      const raw = node.textContent ?? '';
      const normalized = raw.replace(/\s+/g, ' ');
      if (normalized.trim().length > 0) {
        container.push({ type: 'text', content: normalized });
      }
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      const el = node as HTMLElement;
      switch (el.tagName.toLowerCase()) {
        case 'p': {
          const paragraph: PdfElement = { type: 'paragraph', children: [] };
          for (const child of Array.from(el.childNodes)) {
            processNode(child, paragraph.children);
          }
          if (paragraph.children.length > 0) {
            container.push(paragraph);
          }
          break;
        }
        case 'h1':
        case 'h2':
        case 'h3':
        case 'h4':
        case 'h5':
        case 'h6':
          container.push({
            type: 'heading',
            level: parseInt(el.tagName.substring(1)),
            content: el.textContent?.trim() ?? ''
          });
          break;
        case 'strong':
        case 'b':
          container.push({
            type: 'text',
            content: (el.textContent ?? '').replace(/\s+/g, ' ').trim(),
            style: 'bold'
          });
          break;
        case 'em':
        case 'i':
          container.push({
            type: 'text',
            content: (el.textContent ?? '').replace(/\s+/g, ' ').trim(),
            style: 'italic'
          });
          break;
        case 'ul':
        case 'ol': {
          const isOrdered = el.tagName.toLowerCase() === 'ol';
          const list: PdfElement = { type: 'list', ordered: isOrdered, items: [] };
          for (const child of Array.from(el.children)) {
            if (child.tagName.toLowerCase() !== 'li') continue;
            const li = child as HTMLElement;
            const listItem: PdfListItem = { content: [] };
            for (const childNode of Array.from(li.childNodes)) {
              processNode(childNode, listItem.content);
            }
            list.items.push(listItem);
          }
          if (list.items.length > 0) {
            container.push(list);
          }
          break;
        }
        case 'br':
          container.push({ type: 'break' });
          break;
        default:
          for (const child of Array.from(el.childNodes)) {
            processNode(child, container);
          }
          break;
      }
    }
  };

  for (const child of Array.from(doc.body.childNodes)) {
    processNode(child);
  }
  return elements;
}

function ensurePdfElements(field: any): PdfElement[] {
  if (!field) return [];
  if (Array.isArray(field) && field.length > 0 && typeof field[0] === 'object' && 'type' in field[0]) {
    return field;
  }
  if (typeof field === 'string') {
    if (field.trim().startsWith('<')) {
      const cleanHtml = sanitizeHtml(field);
      return parseHtmlToElementsSync(cleanHtml);
    }
    return [{ type: 'text', content: field }];
  }
  return [{ type: 'text', content: String(field) }];
}

function renderTargetAndEndpoints(
  scope: string,
  targetDetails: any[],
  findingTargetIds?: any[],
  findingEndpointIds?: any[],
  findingEndpointValues?: any[]
) {
  if (scope) {
    const cleanScope = scope.replace(/<[^>]*>/g, '').trim();
    if (cleanScope.toLowerCase() === 'n/a') {
      return 'N/A';
    }
    return cleanScope;
  }
  if (findingTargetIds && findingTargetIds.length > 0) {
    const fallbackLabels: string[] = findingTargetIds.map(tid => {
      const match = (targetDetails || []).find((td: any) => String(td.target_id) === String(tid));
      return match
        ? (match.target_value || match.target_name || match.url || String(tid))
        : String(tid);
    }).filter(Boolean);
    if (fallbackLabels.length > 0) {
      return fallbackLabels.join(', ');
    }
  }
  return 'N/A';
}

const DetailedFindingsBySeverityPageV2: React.FC<DetailedFindingsBySeverityPageProps> = ({
  severity,
  findings,
  documentNumber,
  reportData,
  sectionId,
  showMainTitle = false
}) => {
  const normSev = normalizeSeverity(severity);
  const activeColor = severityColors[normSev] || '#64748B';

  if (!findings || findings.length === 0) return null;

  return (
    <>
      {findings.map((finding, idx) => {
        const title = finding.title || 'Untitled Finding';
        const fid = finding.report_id || finding.id || `F-${idx + 1}`;

        let score: string = 'N/A';
        let vector: string = 'N/A';

        const isV4 = finding.cvss_version === '4.0' || (finding.cvss_vector && finding.cvss_vector.startsWith('CVSS:4.0'));

        if (finding.cvss_vector && typeof finding.cvss_vector === 'string' && finding.cvss_vector.includes('CVSS')) {
          vector = finding.cvss_vector;
          if (finding.cvss_score != null) {
            score = String(finding.cvss_score);
          } else {
            try {
              if (isV4) {
                const calc40 = new CVSS40(vector);
                score = calc40.Score().toFixed(1);
              } else {
                const calc = (CVSS31 as any).calculateCVSSFromVector(finding.cvss_vector);
                if (calc && calc.baseMetricScore) {
                  score = String(calc.baseMetricScore);
                }
              }
            } catch (_) {}
          }
        } else if (finding.cvss_score != null) {
          score = String(finding.cvss_score);
        } else {
          const cvssVector = (finding.severity && typeof finding.severity === 'string' && finding.severity.includes('CVSS'))
            ? finding.severity : '';
          if (cvssVector) {
            vector = cvssVector;
            try {
              if (isV4 || cvssVector.startsWith('CVSS:4.0')) {
                const calc40 = new CVSS40(cvssVector);
                score = calc40.Score().toFixed(1);
              } else {
                const calc = (CVSS31 as any).calculateCVSSFromVector(cvssVector);
                if (calc && calc.baseMetricScore) {
                  score = String(calc.baseMetricScore);
                }
              }
            } catch (_) {}
          }
        }

        const cweRaw = finding.owasp_category || finding.owasp || finding.category || 'N/A';
        const cwe = cweRaw !== 'N/A' ? parseCategoryString(cweRaw) : 'N/A';
        const status = finding.status || 'Open';
        const hasScreenshots = Array.isArray(finding.attachments) && finding.attachments.length > 0;

        return (
          <Page size="A4" key={idx} style={styles.page}>
            {/* Page Title Block (Renders only on the first page of detailed findings) */}
            {showMainTitle && idx === 0 && (
              <View style={{ marginBottom: 12 }}>
                <Text style={styles.mainTitle}>7. Detailed Findings</Text>
                <View style={styles.titleUnderlineRow}>
                  <View style={styles.titleUnderlineBlue} />
                  <View style={styles.titleUnderlineDark} />
                </View>
                <Text style={styles.mainSubtitle}>
                  Each finding uses a consistent layout. Duplicate a card for every new issue.
                </Text>
              </View>
            )}

            {/* Card Container */}
            <View style={styles.card}>
              {/* Card Header */}
              <View style={styles.cardHeader}>
                <View style={styles.cardHeaderLeft}>
                  <Text style={styles.cardFindingId}>{fid}</Text>
                  <Text style={styles.cardTitle}>{title}</Text>
                </View>
                <View style={styles.cardHeaderRight}>
                  <View style={[styles.severityPill, { backgroundColor: activeColor }]}>
                    <Text style={styles.severityPillText}>{normSev}</Text>
                  </View>
                  <Text style={styles.cvssLabel}>{score !== 'N/A' ? score : 'N/A'}</Text>
                </View>
              </View>

              {/* Details Grid */}
              <View style={styles.detailsGrid}>
                {/* OWASP / CWE Row */}
                <View style={styles.detailsRow}>
                  <Text style={styles.detailsLabel}>OWASP / CWE</Text>
                  <Text style={styles.detailsValue}>{cwe}</Text>
                </View>
                
                {/* CVSS Vector Row */}
                <View style={styles.detailsRow}>
                  <Text style={styles.detailsLabel}>CVSS Vector</Text>
                  <View style={styles.detailsValue}>
                    {vector !== 'N/A' ? (
                      <View style={styles.cvssVectorPill}>
                        <Text style={styles.cvssVectorPillText}>{vector}</Text>
                      </View>
                    ) : (
                      <Text style={{ fontSize: 8.5 }}>N/A</Text>
                    )}
                  </View>
                </View>

                {/* Affected Endpoint Row */}
                <View style={styles.detailsRow}>
                  <Text style={styles.detailsLabel}>Affected Endpoint</Text>
                  <Text style={styles.detailsValue}>
                    {renderTargetAndEndpoints(
                      finding.scope,
                      reportData?.target_details || [],
                      finding.target_ids,
                      finding.endpoint_ids,
                      finding.endpoint_values
                    )}
                  </Text>
                </View>

                {/* Status Row */}
                <View style={styles.detailsRow}>
                  <Text style={styles.detailsLabel}>Status</Text>
                  <Text style={[styles.detailsValue, { fontWeight: 'bold' }]}>{status}</Text>
                </View>
              </View>

              {/* DESCRIPTION / ROOT CAUSE */}
              {finding.description && (
                <View style={styles.sectionBox}>
                  <Text style={styles.sectionTitle} wrap={false}>Description / Root Cause</Text>
                  <View style={styles.contentWrapper}>
                    {renderPdfElements(ensurePdfElements(finding.description))}
                  </View>
                </View>
              )}

              {/* BUSINESS IMPACT */}
              {finding.impact && (
                <View style={styles.sectionBox}>
                  <Text style={styles.sectionTitle} wrap={false}>Business Impact</Text>
                  <View style={styles.contentWrapper}>
                    {renderPdfElements(ensurePdfElements(finding.impact))}
                  </View>
                </View>
              )}

              {/* STEPS TO REPRODUCE */}
              {finding.instructions && (
                <View style={styles.sectionBox}>
                  <Text style={styles.sectionTitle} wrap={false}>Steps to Reproduce</Text>
                  <View style={styles.contentWrapper}>
                    {renderPdfElements(ensurePdfElements(finding.instructions))}
                  </View>
                </View>
              )}

              {/* REMEDIATION */}
              {finding.fix && (
                <View style={styles.sectionBox}>
                  <Text style={styles.sectionTitle} wrap={false}>Remediation</Text>
                  <View style={styles.contentWrapper}>
                    {renderPdfElements(ensurePdfElements(finding.fix))}
                  </View>
                </View>
              )}
            </View>

            {/* Footer */}
            <View style={styles.footer} fixed>
              <Text style={styles.footerLeft}>{documentNumber || 'Pentest Report'}</Text>
              <Text style={styles.footerRight} render={({ pageNumber, totalPages }) => `${pageNumber} / ${totalPages}`} />
            </View>
          </Page>
        );
      })}
    </>
  );
};

export default DetailedFindingsBySeverityPageV2;
